import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { proHydroApi } from '../../../helpers/crude_api_helper';

// Backend entity: TemplateInformationCategory ("Klausa Category").
// See api_contract/api-contract-klausa-category.md.
//
//   list       GET    /v1/TemplateInformationCategories   (paginated)
//   create     POST   /v1/TemplateInformationCategories   (JSON)
//   update     PUT    /v1/TemplateInformationCategories/{id} (JSON)
//   delete     DELETE /v1/TemplateInformationCategories/{id}
//   sort-order PUT    /v1/TemplateInformationCategories/sort-order (JSON)
//   lookup     GET    /v1/JenisPengadaans/Lookup          -> [{ id, text }]
//
// Field mapping: API `name` <-> UI "Category Name"; `description` <-> "Description".
//
// Contract quirks handled here:
//   - Create/Update expect a JSON body (form-urlencoded was tried first but the
//     server returns 415 Unsupported Media Type for this endpoint).
//   - `isActive` is IGNORED on create (server forces true) -> no Status field on Add.
//   - `categories` must NOT be sent on Update (ignored by the handler).
//   - Duplicate (jenisPengadaanId + name) -> 409; not-found / id-mismatch -> 422 (not 404).
//   - JenisPengadaanCode/Name cannot be sorted or searched server-side.
const ENDPOINT = '/v1/TemplateInformationCategories';
const JENIS_PENGADAAN_LOOKUP = '/v1/JenisPengadaans/Lookup';

// sortField must be PascalCase and inside the API whitelist:
// Id, JenisPengadaanId, Name, Description, IsActive, SortOrder, IsDeleted.
const toPascalCase = field =>
  field ? field.charAt(0).toUpperCase() + field.slice(1) : field;

const emptyForm = {
  id: null,
  jenisPengadaanId: '',
  jenisPengadaanName: '',
  name: '',
  description: '',
  isActive: true,
};

const ClausaCategoryView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jenisOptions, setJenisOptions] = useState([]);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  document.title = 'Clausa Category';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // The list is paginated server-side; without page/pageSize we would
      // silently only ever see the first 10 rows.
      const params = { page: currentPage, pageSize: rowsPerPage };
      if (debouncedSearch) params.searchText = debouncedSearch;
      if (sortField) {
        params.sortField = sortField;
        params.sortOrder = sortDirection === 'asc' ? 'Asc' : 'Desc';
      }
      const result = await proHydroApi.get(ENDPOINT, params);
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalRows(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, debouncedSearch, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchJenisPengadaan();
  }, []);

  // Lookup returns { items: [{ id, text }] } where `text` is already
  // "CODE - Name". `id` is the real GUID required by create/update.
  const fetchJenisPengadaan = async () => {
    try {
      const result = await proHydroApi.get(JENIS_PENGADAAN_LOOKUP);
      setJenisOptions(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      toast.error(`Failed to load Jenis Pengadaan: ${error}`);
      setJenisOptions([]);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleStatusChange = e => {
    setFormData({ ...formData, isActive: e.target.checked });
  };

  // No client-side duplicate check: the list is paginated, so `items` only
  // holds the current page and could not see a clashing name on another page.
  // The server enforces (jenisPengadaanId + name) uniqueness and returns 409.
  const validateForm = () => {
    const newErrors = {};
    if (!formData.jenisPengadaanId) {
      newErrors.jenisPengadaanId = 'Jenis Pengadaan is required';
    }
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Category Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setModalForm(true);
  };

  const handleUpdateModal = row => {
    setFormData({
      id: row.id,
      jenisPengadaanId: row.jenisPengadaanId || '',
      jenisPengadaanName: row.jenisPengadaanName || '',
      name: row.name || '',
      description: row.description || '',
      isActive: !!row.isActive,
    });
    setErrors({});
    setModalForm(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (formData.id) {
      try {
        // JSON body (BE returns 415 on form-urlencoded here). `categories` is
        // deliberately NOT sent (ignored by the update handler). An unknown
        // jenisPengadaanId is silently ignored by the server, which is why the
        // dropdown only offers Lookup values.
        await proHydroApi.put(`${ENDPOINT}/${formData.id}`, {
          id: formData.id,
          jenisPengadaanId: formData.jenisPengadaanId,
          name: formData.name.trim(),
          description: formData.description || '',
          isActive: formData.isActive,
        });
        toast.success('Clausa Category has been updated');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to update data: ${error}`);
      }
    } else {
      try {
        // JSON body (BE returns 415 on form-urlencoded here). `isActive` is
        // omitted: the server forces true on create and ignores the field
        // entirely.
        await proHydroApi.create(ENDPOINT, {
          jenisPengadaanId: formData.jenisPengadaanId,
          name: formData.name.trim(),
          description: formData.description || '',
        });
        toast.success('Clausa Category has been added');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      }
    }
  };

  const handleDeleteModal = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await proHydroApi.delete(`${ENDPOINT}/${selectedRow.id}`);
      toast.success('Clausa Category has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  // sort-order is the only JSON endpoint here. The handler reads just id +
  // sortOrder and ignores unknown ids, so swapping the two affected rows is
  // enough — no need to resend the whole list like the Blazor grid does.
  //
  // Reorder is scoped to the loaded page: the neighbouring row on another page
  // isn't in memory to swap with.
  const moveRow = async (row, direction) => {
    const index = items.findIndex(item => item.id === row.id);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= items.length) return;

    const current = items[index];
    const swapWith = items[swapIndex];

    try {
      await proHydroApi.put(`${ENDPOINT}/sort-order`, {
        categories: [
          { id: current.id, sortOrder: swapWith.sortOrder },
          { id: swapWith.id, sortOrder: current.sortOrder },
        ],
      });
      fetchData();
    } catch (error) {
      toast.error(`Failed to reorder data: ${error}`);
    }
  };

  const handleSort = (column, direction) => {
    setSortField(column.id ? toPascalCase(column.id) : null);
    setSortDirection(direction);
  };

  // A category can point at a soft-deleted JenisPengadaan, which the Lookup
  // (filtered to !IsDeleted) won't return — surface it so editing such a row
  // doesn't silently blank the field.
  const jenisSelectOptions = (() => {
    const known = jenisOptions.some(o => o.id === formData.jenisPengadaanId);
    if (formData.jenisPengadaanId && !known) {
      return [
        {
          id: formData.jenisPengadaanId,
          text: formData.jenisPengadaanName || '(current)',
        },
        ...jenisOptions,
      ];
    }
    return jenisOptions;
  })();

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      // Not sortable: the server maps JenisPengadaanCode/Name through a
      // conditional join expression, so it can't ORDER BY (or search) it.
      name: headerLabel('Jenis Pengadaan'),
      selector: row => row.jenisPengadaanName,
      wrap: true,
    },
    {
      id: 'name',
      name: headerLabel('Category Name'),
      selector: row => row.name,
      sortable: true,
      wrap: true,
    },
    {
      id: 'description',
      name: headerLabel('Description'),
      selector: row => row.description,
      sortable: true,
      wrap: true,
    },
    {
      id: 'isActive',
      name: headerLabel('Status'),
      width: '110px',
      center: true,
      sortable: true,
      cell: row => (
        <div className='form-check d-flex justify-content-center'>
          <Input
            type='checkbox'
            className='form-check-input'
            checked={!!row.isActive}
            readOnly
            disabled
          />
        </div>
      ),
    },
    {
      name: headerLabel('Order'),
      width: '110px',
      center: true,
      cell: row => {
        const index = items.findIndex(item => item.id === row.id);
        return (
          <div className='d-flex gap-1'>
            <button
              type='button'
              className='btn btn-soft-secondary btn-sm btn-icon'
              disabled={index <= 0}
              onClick={() => moveRow(row, 'up')}
            >
              <i className='ri-arrow-up-line align-bottom'></i>
            </button>
            <button
              type='button'
              className='btn btn-soft-secondary btn-sm btn-icon'
              disabled={index === items.length - 1}
              onClick={() => moveRow(row, 'down')}
            >
              <i className='ri-arrow-down-line align-bottom'></i>
            </button>
          </div>
        );
      },
    },
    {
      name: headerLabel('Action'),
      width: '110px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleUpdateModal(row)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDeleteModal(row)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='Clausa Category' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='ClausaCategoryList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Clausa Category
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                          />
                        </div>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={handleNewModal}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>
                            Add Clausa Category
                          </span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No clausa category found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus={true}
            centered
            id='addClausaCategoryModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold'>
                {formData.id !== null
                  ? 'Edit Clausa Category'
                  : 'Add Clausa Category'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='jenisPengadaanId' className='form-label'>
                          Jenis Pengadaan <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='select'
                          id='jenisPengadaanId'
                          name='jenisPengadaanId'
                          className='form-control'
                          invalid={!!errors.jenisPengadaanId}
                          value={formData.jenisPengadaanId || ''}
                          onChange={handleChange}
                        >
                          <option value=''>Select Jenis Pengadaan</option>
                          {jenisSelectOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.text}
                            </option>
                          ))}
                        </Input>
                        {errors.jenisPengadaanId && (
                          <div className='invalid-feedback d-block'>
                            {errors.jenisPengadaanId}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='name' className='form-label'>
                          Category Name <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='name'
                          name='name'
                          className='form-control'
                          maxLength={100}
                          invalid={!!errors.name}
                          value={formData.name || ''}
                          onChange={handleChange}
                        />
                        {errors.name && (
                          <div className='invalid-feedback d-block'>
                            {errors.name}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='description' className='form-label'>
                          Description
                        </Label>
                        <Input
                          type='text'
                          id='description'
                          name='description'
                          className='form-control'
                          maxLength={200}
                          value={formData.description || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </Col>
                    {/* No Status field on Add: the server ignores `isActive` on
                        create and always stores true. */}
                    {formData.id !== null && (
                      <Col sm={12}>
                        <div className='mb-3'>
                          <div className='form-check'>
                            <Input
                              type='checkbox'
                              id='isActive'
                              name='isActive'
                              className='form-check-input'
                              checked={!!formData.isActive}
                              onChange={handleStatusChange}
                            />
                            <Label for='isActive' className='form-check-label'>
                              Active
                            </Label>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='light'
                className='btn-sm'
                onClick={() => setModalForm(false)}
              >
                Cancel
              </Button>
              <Button color='primary' className='btn-sm' onClick={handleSave}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ClausaCategoryView;
